/* eslint-disable no-console */
import { gql, useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@components/Alert';
import { TagsCollectionType } from '@components/Chat/TagsCollection';
import {
  Chat,
  ChatUpdateDocument,
  ListChatsPaginationDocument,
  LocalFile,
  Message,
  MessageCheckin,
  Task,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import useMe from '@hooks/useMe';
import useSearch from '@hooks/useSearch';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { ProjectTagType } from '@src/types/project';
import { TaskTagType } from '@src/types/task';
import {
  bumpChatToTop,
  isChatInCachedList,
  isLocalClockActivity,
} from '@utils/chat/bumpChatToTop';
import {
  isAfterReadThrough,
  setSuppressedUnreadRestoreHandler,
  shouldIgnoreUnreadUpdate,
  stashSuppressedUnreadUpdate,
} from '@utils/chat/chatReadGuard';
import { resubscribeDelayMs } from '@utils/chat/subscriptionBackoff';
import { useFilePicker, validateFiles } from '@utils/filePicker';
import { fileToLocalFile } from '@utils/fileUtils';
import { useFileValidation } from '@utils/fileValidationUtils';
import { compressImages } from '@utils/imageCompression';
import { TextSelection } from '@utils/insertAtSelection';
import { AssignTaskSourceCard } from '@utils/tasks/assignTaskSourceCard';
import { UploadTagOnboardingStep } from '@utils/uploadTagOnboarding';

type ChatInputWithChatId = {
  activeChatId: string;
  chatInput: ChatInputItem;
};

type ChatInputItem = {
  message: string;
  tagsCollection: TagsCollectionType[] | undefined;
  localFiles: LocalFile[];
  replyMessage: Message | undefined;
};

type ChatInputContext = {
  getReplyMessage: (chatId: Chat['id']) => Message | undefined;
  getTagsCollection: (chatId: Chat['id']) => TagsCollectionType[];
  getLocalFiles: (chatId: Chat['id']) => LocalFile[];
  chatInputs: ChatInputWithChatId[];
  setChatInputs: React.Dispatch<React.SetStateAction<ChatInputWithChatId[]>>;
  isTagModalOpen: boolean;
  setIsTagModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCompletingTask: boolean;
  setIsCompletingTask: React.Dispatch<React.SetStateAction<boolean>>;
  isAssigningTask: boolean;
  setIsAssigningTask: React.Dispatch<React.SetStateAction<boolean>>;
  isAssigningTaskId: Task['id'] | undefined;
  setIsAssigningTaskId: React.Dispatch<
    React.SetStateAction<Task['id'] | undefined>
  >;
  // Web: the MEMBER JOINED card the current assign flow was opened from, so
  // the assignment can replace it (TA-11038). Undefined for other entries.
  assignTaskSourceCard: AssignTaskSourceCard | undefined;
  setAssignTaskSourceCard: React.Dispatch<
    React.SetStateAction<AssignTaskSourceCard | undefined>
  >;
  isConfirmCompletingTask: boolean;
  setIsConfirmCompletingTask: React.Dispatch<React.SetStateAction<boolean>>;
  isAssigningTaskStep2: boolean;
  setIsAssigningTaskStep2: React.Dispatch<React.SetStateAction<boolean>>;
  existingTagsCollection: TagsCollectionType[] | undefined;
  setExistingTagsCollection: React.Dispatch<
    React.SetStateAction<TagsCollectionType[] | undefined>
  >;
  filterVal: string;
  setFilterVal: React.Dispatch<React.SetStateAction<string>>;
  selectedMessage: Message | undefined;
  setSelectedMessage: React.Dispatch<React.SetStateAction<Message | undefined>>;
  selectedChat: Chat | undefined;
  setSelectedChat: React.Dispatch<React.SetStateAction<Chat | undefined>>;
  selectedReplyMessage: Message | undefined;
  setSelectedReplyMessage: React.Dispatch<
    React.SetStateAction<Message | undefined>
  >;
  setMessage: (message: string) => void;
  setTagsCollection: (
    chatId: Chat['id'],
    tagsCollection: TagsCollectionType[]
  ) => void;
  updateTagsCollectionTasks: (chatId: Chat['id'], task: TaskTagType) => void;
  updateTagsCollectionProject: (
    chatId: Chat['id'],
    project: ProjectTagType
  ) => void;
  removeTagsCollection: (chatId: Chat['id'], indexToDelete: number) => void;
  removeTagsCollectionTask: (
    chatId: Chat['id'],
    tagIndex: number,
    collectionIndex: number
  ) => void;
  setLocalFiles: (chatId: Chat['id'], files: LocalFile[]) => Promise<void>;
  setReplyMessage: (chatId: Chat['id'], message?: Message) => void;
  pickChatImages: (chatId: Chat['id']) => Promise<void>;
  pickChatFiles: (chatId: Chat['id']) => Promise<void>;
  launchChatCamera: (chatId: Chat['id']) => Promise<void>;
  focus: boolean;
  setFocus: React.Dispatch<React.SetStateAction<boolean>>;
  modalHeight: number;
  setModalHeight: React.Dispatch<React.SetStateAction<number>>;
  messageModalHeight: number;
  setMessageModalHeight: React.Dispatch<React.SetStateAction<number>>;
  shouldShowTutorialHashTag: boolean;
  setShouldShowTutorialHashTag: React.Dispatch<React.SetStateAction<boolean>>;
  doNotShowTutorial: boolean;
  setDoNotShowTutorial: React.Dispatch<React.SetStateAction<boolean>>;
  showChatOptionsBar: boolean;
  setShowChatOptionsBar: React.Dispatch<React.SetStateAction<boolean>>;
  dropFileHandler: (chatId: Chat['id'], files: File[]) => Promise<void>;
  playingVoiceMemoId: string;
  setPlayingVoiceMemoId: React.Dispatch<React.SetStateAction<string>>;
  subscribeToChatUpdates: () => () => void;
  getCameraPhoto: (chatId: Chat['id'], r: LocalFile[]) => void;
  hasCameraPermission: boolean;
  setHasCameraPermission: React.Dispatch<React.SetStateAction<boolean>>;
  refreshActivity: boolean;
  setRefreshActivity: React.Dispatch<React.SetStateAction<boolean>>;
  myChatLocalFiles: LocalFile[];
  setMyChatLocalFiles: React.Dispatch<React.SetStateAction<LocalFile[]>>;
  myChatCheckin: MessageCheckin | undefined;
  setMyChatCheckin: React.Dispatch<
    React.SetStateAction<MessageCheckin | undefined>
  >;
  editMedia: LocalFile[];
  setEditMedia: React.Dispatch<React.SetStateAction<LocalFile[]>>;
  // First-attachment tag onboarding flow — shared across the chat composer,
  // the my-tag-modal screen and the chat screen (see @utils/uploadTagOnboarding)
  uploadTagOnboardingStep: UploadTagOnboardingStep;
  setUploadTagOnboardingStep: React.Dispatch<
    React.SetStateAction<UploadTagOnboardingStep>
  >;
  // Last known caret/selection of the web composer, captured before focus
  // moves to the emoji picker so the emoji lands at the caret (TA-11126).
  messageSelectionRef: React.MutableRefObject<TextSelection | null>;
} & Pick<ChatInputItem, 'message'>;

const chatInputContext = createContext<ChatInputContext | undefined>(undefined);

const MAX_DOC_COUNT = 9;
const MAX_IMAGE_COUNT = 12;
export const MAX_RECORDING_SECONDS = 120;

export const ChatInputProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { setActiveMessageId } = useActiveChat();
  const { t } = useTranslation();
  const { launchImageSelection, launchFileSelection } = useFilePicker();
  const { isValidExtension } = useFileValidation();
  const { Provider } = chatInputContext;
  const { activeChat } = useActiveChat();
  const [chatInputs, setChatInputs] = useState<ChatInputWithChatId[]>([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState<boolean>(false);
  const [isCompletingTask, setIsCompletingTask] = useState<boolean>(false);
  const [isAssigningTask, setIsAssigningTask] = useState<boolean>(false);
  const [isConfirmCompletingTask, setIsConfirmCompletingTask] =
    useState<boolean>(false);
  const [isAssigningTaskStep2, setIsAssigningTaskStep2] =
    useState<boolean>(false);
  const [assignTaskSourceCard, setAssignTaskSourceCard] = useState<
    AssignTaskSourceCard | undefined
  >();
  // Once the assign-task flow is fully closed (cancel, outside click, back),
  // drop the card it was opened from so a later assign in the same project
  // doesn't swap that card. Step 1 → step 2 flips both flags in one batch.
  useEffect(() => {
    if (!isAssigningTask && !isAssigningTaskStep2) {
      setAssignTaskSourceCard(undefined);
    }
  }, [isAssigningTask, isAssigningTaskStep2]);
  const [isAssigningTaskId, setIsAssigningTaskId] = useState<
    Task['id'] | undefined
  >(undefined);
  const [existingTagsCollection, setExistingTagsCollection] =
    useState<TagsCollectionType[]>();
  const [filterVal, setFilterVal] = useState<string>('');
  const { me } = useMe();
  const [selectedMessage, setSelectedMessage] = useState<Message>();
  const [selectedChat, setSelectedChat] = useState<Message>();
  const [selectedReplyMessage, setSelectedReplyMessage] = useState<Message>();
  const [focus, setFocus] = useState<boolean>(false);
  const [modalHeight, setModalHeight] = useState<number>(0);
  const [messageModalHeight, setMessageModalHeight] = useState<number>(0);
  const [shouldShowTutorialHashTag, setShouldShowTutorialHashTag] =
    useState<boolean>(false);
  const [doNotShowTutorial, setDoNotShowTutorial] = useState<boolean>(false);
  const [showChatOptionsBar, setShowChatOptionsBar] = useState<boolean>(false);
  const navigation = useAppNavigation();
  const [playingVoiceMemoId, setPlayingVoiceMemoId] = useState<''>();
  const [hasCameraPermission, setHasCameraPermission] =
    useState<boolean>(false);
  const client = useApolloClient();
  const { directUploadFiles } = useChatUploadManager();
  const [refreshActivity, setRefreshActivity] = useState<boolean>(false);
  const { isSearchPreview, setIsSearchPreview } = useSearch();
  const [myChatLocalFiles, setMyChatLocalFiles] = useState<LocalFile[]>([]);
  const [myChatCheckin, setMyChatCheckin] = useState<
    MessageCheckin | undefined
  >();
  const [editMedia, setEditMedia] = useState<LocalFile[]>([]);
  const [uploadTagOnboardingStep, setUploadTagOnboardingStep] =
    useState<UploadTagOnboardingStep>('idle');
  const messageSelectionRef = useRef<TextSelection | null>(null);

  // Drafts are per chat but the caret ref is shared; drop the old chat's
  // offsets so a pick in the new chat appends instead of using them.
  useEffect(() => {
    messageSelectionRef.current = null;
  }, [activeChat?.id]);

  const initialChatInput = {
    message: '',
    tagsCollection: [],
    localFiles: [],
    replyMessage: undefined,
  };

  const currentChatInput = () => {
    return (
      chatInputs?.find((i) => i.activeChatId === activeChat?.id)?.chatInput ||
      initialChatInput
    );
  };

  const findChatInput = (chatId: Chat['id']) => {
    return (
      chatInputs?.find((i) => i.activeChatId === chatId)?.chatInput ||
      initialChatInput
    );
  };

  const createDraftInput = () => {
    // if no draft exists, create one
    if (!chatInputs.find((i) => i.activeChatId === activeChat?.id)) {
      setChatInputs((chats) => [
        ...chats,
        {
          activeChatId: activeChat?.id || '',
          chatInput: {
            ...initialChatInput,
          },
        },
      ]);
    }
  };

  const updateChatInputs = (newChatInput: ChatInputItem) => {
    const index = chatInputs.findIndex(
      (x) => x.activeChatId === activeChat?.id
    );
    if (index) {
      setChatInputs((curr) => [
        ...curr.slice(0, index),
        { ...curr[index], ...newChatInput },
        ...curr.slice(index + 1),
      ]);
    }
  };

  const updateChatInputsById = (
    chatId: Chat['id'],
    newChatInput: ChatInputItem
  ) => {
    const index = chatInputs.findIndex((x) => x.activeChatId === chatId);
    if (index) {
      setChatInputs((curr) => [
        ...curr.slice(0, index),
        { ...curr[index], ...newChatInput },
        ...curr.slice(index + 1),
      ]);
    }
  };

  // Restores a count the guard suppressed while a markChatsRead was in flight
  // and then judged to postdate the receipt — see chatReadGuard. Registered
  // for the life of the provider, NOT inside subscribeToChatUpdates:
  // ChatsData subscribes and unsubscribes on focus, and a teardown between a
  // suppressed event and its receipt resolving would leave the stash with
  // nowhere to go, hiding the badge until the next refetch.
  //
  // Raises only. By the time it runs the user may have opened the chat, or a
  // newer event may have written a higher count, and neither should be undone
  // (opening also fires its own markChatsRead through the same coalescer,
  // whose newer watermark reads through the stashed event and cancels the
  // restore).
  useEffect(() => {
    setSuppressedUnreadRestoreHandler((chatId, unreadCount) => {
      const cacheId = client.cache.identify({ __typename: 'Chat', id: chatId });
      if (!cacheId) return;
      client.cache.modify({
        id: cacheId,
        fields: {
          unreadCount: (existing: number | null) =>
            unreadCount > (existing ?? 0) ? unreadCount : existing,
        },
      });
    });
    return () => setSuppressedUnreadRestoreHandler(null);
  }, [client]);

  /**
   * TA-10877: an errored Observable is terminated and nothing used to restart
   * it, so a single fatal WebSocket close left the chat list with no real-time
   * updates for the rest of the session — only a reload recovered. graphql-ws
   * (v5.16.2) treats a 4401 Unauthorized close as fatal: its internal
   * `shouldRetryConnectOrThrow` never consults our `shouldRetry`
   * (useApolloClient.tsx), so the library will not reconnect and the error is
   * delivered to each active subscription's own `error` handler.
   *
   * Mirrors the `projectUpdated` treatment from TA-10117 / TA-10670:
   * resubscribe with exponential backoff, reset the backoff on a healthy
   * event, and never leave overlapping timers behind. A genuinely revoked
   * token still logs the user out via the WS link's own 4401 handling, which
   * unmounts the screen holding this subscription.
   */
  const subscribeToChatUpdates = (): (() => void) => {
    let disposed = false;
    let activeSub: { unsubscribe: () => void } | null = null;
    let resubscribeTimeout: ReturnType<typeof setTimeout> | null = null;
    let resubscribeAttempt = 0;

    const scheduleResubscribe = () => {
      if (disposed) return;
      // This subscription is terminated after `error`, so a second call should
      // not happen — clearing first costs nothing and rules out two timers
      // racing to open two sockets.
      if (resubscribeTimeout) clearTimeout(resubscribeTimeout);
      const delay = resubscribeDelayMs(resubscribeAttempt++);
      resubscribeTimeout = setTimeout(() => {
        resubscribeTimeout = null;
        if (!disposed) subscribe();
      }, delay);
    };

    const subscribe = () => {
      if (disposed) return;
      activeSub = client
        .subscribe({
          query: ChatUpdateDocument,
          // The default 'none' makes Apollo throw on any payload carrying
          // `errors`, which terminates the stream permanently — one
          // error-bearing frame was enough to take the chat list dark even
          // without a socket close (TA-10877). 'all' delivers the payload and
          // keeps the stream alive.
          errorPolicy: 'all',
        })
        .subscribe({
          next({ data, errors }) {
            // A delivered event proves the socket is healthy again, so a later
            // unrelated error does not inherit an inflated delay.
            resubscribeAttempt = 0;

            if (errors?.length) {
              Sentry.captureMessage(
                `chatUpdate subscription payload errors: ${errors
                  .map((error) => error.message)
                  .join('; ')}`,
                'warning'
              );
            }

            const chatUpdate = data?.chatUpdate;
            if (!chatUpdate?.id) return;

            try {
              const chatCacheId = client.cache.identify({
                __typename: 'Chat',
                id: chatUpdate.id,
              });

              // Read BEFORE writing so the outdated-event check is meaningful.
              const existingRecent = client.cache.readFragment<{
                recentActivityAt?: string | null;
              }>({
                id: chatCacheId,
                fragment: gql`
                  fragment _ChatRecent on Chat {
                    recentActivityAt
                  }
                `,
              })?.recentActivityAt;

              // TA-9295: the server computes unreadCount per user, but that
              // computation can run before this device's own markChatsRead
              // commits — the event then carries the pre-read count and writes
              // the badge straight back onto a chat the user is sending from.
              // While that request is in flight the payload's count cannot be
              // trusted to raise the badge. Once it has committed, the payload's
              // recentActivityAt decides: the guard only suppresses events the
              // receipt already read through, so a message someone else sent
              // right after it still shows its badge.
              //
              // Judged before the outdated check below, because that check
              // compares against a device-clock value and must not be the one
              // deciding whether a badge shows.
              const isOutdated =
                !!existingRecent &&
                !!chatUpdate.recentActivityAt &&
                dayjs(chatUpdate.recentActivityAt).isBefore(
                  dayjs(existingRecent)
                );
              const cachedIsLocalBump = isLocalClockActivity(
                chatUpdate.id,
                existingRecent
              );
              const ignoreUnread = shouldIgnoreUnreadUpdate(chatUpdate.id, {
                recentActivityAt: chatUpdate.recentActivityAt,
              });
              // Stash only an event this handler could still apply: one that
              // is not outdated, or outdated only against our own device-clock
              // bump. One outdated against a server-written value is dropped
              // whole below, and a later restore must not resurrect it.
              if (ignoreUnread && (!isOutdated || cachedIsLocalBump)) {
                // In flight there is no watermark yet, so the count is stashed
                // rather than thrown away: the guard judges it once the receipt
                // resolves and calls the restore handler above if the event
                // turned out to postdate the read. On a slow connection that
                // round trip is seconds long, and a real message landing inside
                // it used to lose its badge until the next refetch.
                stashSuppressedUnreadUpdate(chatUpdate.id, {
                  recentActivityAt: chatUpdate.recentActivityAt,
                  unreadCount: chatUpdate.unreadCount,
                });
              }
              const unreadCount = ignoreUnread ? 0 : chatUpdate.unreadCount;

              // Drop outdated subscription events — they carry stale data and
              // must not overwrite a newer timestamp already in the cache.
              //
              // TA-10878: "outdated" includes the echo of this device's own
              // send, whose local bump wrote a client-clock timestamp the
              // server's is a moment behind. Skipping the writes is right;
              // skipping the fallback was not. When that send could not place
              // the chat in the list (below the first page), this event is the
              // first point at which the server actually knows about the
              // message — and the last chance to bring the row in.
              if (isOutdated) {
                // TA-9295 QA round 3: when the cached value is that local
                // bump, it came from the device clock, so "older" is no
                // verdict on this event's unreadCount. With the clock running
                // fast, a message someone else sent right after ours carries
                // a server timestamp behind the bump and landed here —
                // dropped whole, its badge stayed hidden until a refetch. The
                // read guard above judged the count against server-clock
                // values only, so it applies here too.
                //
                // Only then: against a server-written value "older" is real,
                // and a delayed pre-read event must not raise a badge the
                // user has since cleared. And only for an event strictly after
                // the last receipt's watermark: the send path always fires
                // one, and it reads through our own message, so anything
                // dated before our send — a late pre-send event — sits at or
                // below it and stays dropped. No settled receipt, no
                // watermark: fail closed. Raise only, so an out-of-order event
                // cannot lower a newer count either.
                if (
                  !ignoreUnread &&
                  (unreadCount ?? 0) > 0 &&
                  cachedIsLocalBump &&
                  isAfterReadThrough(chatUpdate.id, chatUpdate.recentActivityAt)
                ) {
                  client.cache.modify({
                    id: chatCacheId,
                    fields: {
                      unreadCount: (existing: number | null) =>
                        (unreadCount ?? 0) > (existing ?? 0)
                          ? unreadCount
                          : existing,
                    },
                  });
                }
                if (!isChatInCachedList(client.cache, chatUpdate.id)) {
                  client.refetchQueries({
                    include: [ListChatsPaginationDocument],
                  });
                }
                return;
              }

              // Chat is not in the current page (it has old activity and lives
              // on a later page). bumpChatToTop prepends it directly rather than
              // triggering a full network refetch, which would create a race
              // window where the just-sent message briefly disappears from the
              // detail view.
              const bumped = bumpChatToTop(client.cache, {
                chatId: chatUpdate.id,
                recentActivityAt: chatUpdate.recentActivityAt,
                unreadCount,
                messageBody: chatUpdate.messageBody,
              });

              // TA-10627: bumpChatToTop refuses to prepend an off-page chat the
              // cache cannot render a row for, and this payload cannot supply
              // those fields — chatUpdate returns the `ChatUpdate` type, which
              // carries no avatarUrl/isGroupChat/createdAt and does not even
              // normalize onto the Chat entity. Without this fallback a message
              // into a long-quiet chat would not surface until some unrelated
              // refetch ran. The chat still reaches the top, over the network
              // rather than as a synthetic edge.
              // TA-10876: `bumped` is now true only when the chat was
              // verifiably written into a cached connection. It also covers
              // the case where ROOT_QUERY has no `listChatsPagination` field
              // at all — a failed initial list fetch — which used to report
              // success and drop the event, leaving the list dark until a
              // reload.
              if (!bumped) {
                client.refetchQueries({
                  include: [ListChatsPaginationDocument],
                });
              }
            } catch (error) {
              Sentry.captureException(
                error instanceof Error ? error : new Error(String(error)),
                {
                  level: 'warning',
                  tags: { subscription: 'chatUpdate', stage: 'cacheUpdate' },
                }
              );
              client.refetchQueries({ include: [ListChatsPaginationDocument] });
            }
          },
          error(err) {
            // captureException, not captureMessage: a templated message folds
            // every distinct WS failure into one Sentry issue keyed on the
            // prefix, so a 4401 becomes indistinguishable from a transport
            // timeout or a server crash — the exact signal this recovery
            // exists to surface. 'warning' keeps it out of the error feed
            // since the stream now recovers on its own.
            Sentry.captureException(
              err instanceof Error ? err : new Error(String(err)),
              {
                level: 'warning',
                tags: { subscription: 'chatUpdate' },
              }
            );
            scheduleResubscribe();
          },
        });
    };

    subscribe();

    return () => {
      disposed = true;
      if (resubscribeTimeout) {
        clearTimeout(resubscribeTimeout);
        resubscribeTimeout = null;
      }
      activeSub?.unsubscribe();
      activeSub = null;
    };
  };

  const resetModal = () => {
    const { message } = currentChatInput();
    // set each one of these methods by chat id

    // clear last index of filter val - 1
    if (filterVal) {
      setMessage(message.replace(`#${filterVal}`, ''));
    } else {
      setMessage(message.replace(`#`, ''));
    }
    setFilterVal('');
    setIsTagModalOpen(false);
  };

  const updateTagsCollectionProject = (
    chatId: Chat['id'],
    project: ProjectTagType
  ) => {
    const updateInput = findChatInput(chatId);

    // exit if exists from another author
    if (existingTagsCollection?.find((e) => e.project?.id === project.id)) {
      return;
    }

    if (
      !updateInput.tagsCollection?.find((t) => t.project?.id === project.id) &&
      me
    ) {
      setTagsCollection(chatId, [
        ...(updateInput.tagsCollection ?? []),
        {
          tasks: [],
          project: { ...project },
          author: me,
        },
      ]);
      resetModal();
    }
  };

  const updateTagsCollectionTasks = (chatId: Chat['id'], task: TaskTagType) => {
    const updateInput = findChatInput(chatId);

    // exit if exists from another author
    if (
      existingTagsCollection?.find((e) =>
        e.tasks.find((t) => t.id === task.id) ? 1 : 0
      )
    ) {
      return;
    }

    const collectionToUpdate = updateInput.tagsCollection?.findIndex(
      (t) => t.project?.id === task.project?.id
    );

    if (collectionToUpdate !== undefined && collectionToUpdate !== -1) {
      // put task into correct coll based on project

      if (
        !updateInput.tagsCollection?.[collectionToUpdate].tasks.find(
          (t) => t.id === task.id
        )
      ) {
        const newTagsColl = [...(updateInput.tagsCollection ?? [])];
        newTagsColl[collectionToUpdate] = {
          ...newTagsColl[collectionToUpdate],
          tasks: [...newTagsColl[collectionToUpdate].tasks, task],
        };

        updateInput.tagsCollection = newTagsColl;
        updateChatInputsById(chatId, updateInput);

        resetModal();
      }
    } else if (me) {
      // new tags coll
      setTagsCollection(chatId, [
        ...(updateInput.tagsCollection ?? []),
        {
          tasks: [task],
          project: task.project,
          author: me,
        },
      ]);
      updateChatInputsById(chatId, updateInput);
      resetModal();
    }
  };

  const removeTagsCollection = (chatId: Chat['id'], indexToDelete: number) => {
    const updateInput = findChatInput(chatId);

    updateInput.tagsCollection = updateInput.tagsCollection?.filter(
      (_i, index) => index !== indexToDelete
    );
    updateChatInputsById(chatId, updateInput);
  };

  const removeTagsCollectionTask = (
    chatId: Chat['id'],
    tagIndex: number,
    collectionIndex: number
  ) => {
    const updateInput = findChatInput(chatId);

    const newTagsColl = [...(updateInput.tagsCollection ?? [])];
    const newTasksColl = newTagsColl?.[collectionIndex].tasks.filter(
      (_i, index) => index !== tagIndex
    );
    newTagsColl[collectionIndex].tasks = newTasksColl;

    updateInput.tagsCollection = newTagsColl;

    updateChatInputsById(chatId, updateInput);

    // if lone project && others' have that project, remove project
    if (newTagsColl[collectionIndex].tasks.length === 0) {
      const projectId = newTagsColl[collectionIndex].project?.id;

      if (
        existingTagsCollection?.find((coll) => coll.project?.id === projectId)
      ) {
        removeTagsCollection(chatId, collectionIndex);
      }
    }
  };

  const setMessage = (message: string) => {
    const updateInput = currentChatInput();
    currentChatInput().message = message;
    updateChatInputs(updateInput);
  };

  const setLocalFiles = async (chatId: Chat['id'], files: LocalFile[]) => {
    const updateInput = findChatInput(chatId);
    updateInput.localFiles = files.map((item) => {
      return {
        ...item,
        blobId: item.blobId ?? '',
      };
    });
    updateChatInputsById(chatId, updateInput);
    const toUpload = files.filter((f) => !f.blobId || f.blobId === '');

    if (toUpload.length > 0) {
      const uploaded = await directUploadFiles(toUpload);

      const current = findChatInput(chatId).localFiles;
      const updatedFiles = current.map((f) => {
        const match = uploaded.find((u) => u.clientId === f.clientId);
        return match ? { ...f, blobId: match.blobId } : f;
      });

      await setLocalFiles(chatId, updatedFiles);
    }
  };

  const setTagsCollection = (
    chatId: Chat['id'],
    tagsCollection: TagsCollectionType[]
  ) => {
    const updateInput = findChatInput(chatId);

    updateInput.tagsCollection = tagsCollection;
    updateChatInputsById(chatId, updateInput);
  };

  const setReplyMessage = (chatId: Chat['id'], message?: Message) => {
    setActiveMessageId(undefined);
    if (isSearchPreview) setIsSearchPreview(false);
    const updateInput = findChatInput(chatId);
    updateInput.replyMessage = message;
    updateChatInputsById(chatId, updateInput);
  };

  const pickChatImages = async (chatId: Chat['id']) => {
    if (findChatInput(chatId).localFiles.some((item) => item.isAudio)) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictAudioImages')
      );
      return;
    }
    if (
      findChatInput(chatId).localFiles.some(
        (item) => !item.isImage && !item.isAudio
      )
    ) {
      Alert.alert(t('shared:alert'), t('models:chat.input.mediaConflictFiles'));
      return;
    }
    const selectableImagesCount =
      MAX_IMAGE_COUNT -
      findChatInput(chatId).localFiles.filter((item) => item.isImage).length;

    if (selectableImagesCount > 0) {
      await launchImageSelection(selectableImagesCount, true).then(
        (r) =>
          r &&
          setLocalFiles(chatId, [...findChatInput(chatId).localFiles, ...r])
      );
    } else {
      Alert.alert(t('models:chat.input.maxSelection'));
    }
  };
  const pickChatFiles = async (chatId: Chat['id']) => {
    if (findChatInput(chatId).localFiles.some((item) => item.isAudio)) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictAudioFiles')
      );
      return;
    }
    if (findChatInput(chatId).localFiles.some((item) => item.isImage)) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictImages')
      );
      return;
    }
    const selectableDocumentsCount =
      MAX_DOC_COUNT -
      findChatInput(chatId).localFiles.filter(
        (item) => !item.isImage && !item.isAudio
      ).length;
    if (selectableDocumentsCount > 0) {
      await launchFileSelection(selectableDocumentsCount, true).then((r) => {
        if (!r) return;

        const documents = r.map((item) => {
          return {
            ...item,
            path: '',
            isAudio: false,
            isImage: false,
          };
        });

        setLocalFiles(chatId, [
          ...findChatInput(chatId).localFiles,
          ...documents,
        ]);
      });
    } else {
      Alert.alert(t('models:chat.input.maxSelection'));
    }
  };

  const getCameraPhoto = (chatId: Chat['id'], r: LocalFile[]) => {
    const selectableImagesCount =
      MAX_IMAGE_COUNT -
      findChatInput(chatId).localFiles.filter((item) => item.isImage).length;
    if (selectableImagesCount > 0) {
      r && setLocalFiles(chatId, [...findChatInput(chatId).localFiles, ...r]);
    } else {
      Alert.alert(t('models:chat.input.maxSelection'));
    }
  };

  const loadCameraComponent = (chatId: Chat['id']) => {
    navigation.navigate('expo-camera-photo', {
      chatId,
      onCallback: () => getCameraPhoto(chatId),
      photoCount:
        MAX_IMAGE_COUNT -
        findChatInput(chatId).localFiles.filter((item) => item.isImage).length,
    });
  };

  const launchChatCamera = async (chatId: Chat['id']) => {
    if (findChatInput(chatId).localFiles.some((item) => item.isAudio)) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictAudioImages')
      );
      return;
    }
    if (findChatInput(chatId).localFiles.some((item) => !item.isImage)) {
      Alert.alert(t('shared:alert'), t('models:chat.input.mediaConflictFiles'));
      return;
    }

    loadCameraComponent(chatId);
  };

  useEffect(() => {
    // create a draft on active chat change if there isn't one already
    createDraftInput();
    setFocus(false);
  }, [activeChat?.id]);

  const dropFileHandler = async (chatId: Chat['id'], files: File[]) => {
    const selectableDocumentsCount =
      MAX_DOC_COUNT -
      currentChatInput().localFiles.filter((item) => !item.isImage).length;

    if (files.length > selectableDocumentsCount) {
      Alert.alert(t('models:chat.input.maxSelection'));
      return;
    }

    try {
      // 1. Convert File[] to LocalFile[]
      //    NOTE: fileToLocalFile should not compress inside; keep it simple.
      const newLocalFiles = await Promise.all(
        files.map((file) => fileToLocalFile(file, true))
      );

      // 2. Compress images only (reuse your existing compressImages)
      //    compressImages will only process items where isImage === true.
      const compressedLocalFiles = await compressImages(newLocalFiles);

      // 3. Validate file types / sizes based on final, compressed files
      const isValid = await validateFiles(
        compressedLocalFiles,
        isValidExtension
      );

      if (!isValid) {
        return;
      }

      // 4. Append to current chat input
      setLocalFiles(chatId, [
        ...currentChatInput().localFiles,
        ...compressedLocalFiles,
      ]);
    } catch (err) {
      console.log('dropFileHandler error', err);
    }
  };

  return (
    <Provider
      value={
        {
          message: currentChatInput().message,
          setMessage,
          isCompletingTask,
          setIsCompletingTask,
          isConfirmCompletingTask,
          isAssigningTask,
          setIsAssigningTask,
          isAssigningTaskId,
          setIsAssigningTaskId,
          assignTaskSourceCard,
          setAssignTaskSourceCard,
          isAssigningTaskStep2,
          setIsAssigningTaskStep2,
          setIsConfirmCompletingTask,
          isTagModalOpen,
          setIsTagModalOpen,
          getTagsCollection: (chatId) => findChatInput(chatId).tagsCollection,
          getLocalFiles: (chatId) => findChatInput(chatId).localFiles,
          setTagsCollection,
          updateTagsCollectionTasks,
          updateTagsCollectionProject,
          removeTagsCollection,
          removeTagsCollectionTask,
          filterVal,
          setFilterVal,
          setLocalFiles,
          selectedMessage,
          setSelectedMessage,
          selectedChat,
          setSelectedChat,
          selectedReplyMessage,
          setSelectedReplyMessage,
          getReplyMessage: (chatId) => findChatInput(chatId).replyMessage,
          setReplyMessage,
          existingTagsCollection,
          setExistingTagsCollection,
          pickChatImages,
          pickChatFiles,
          launchChatCamera,
          focus,
          setFocus,
          modalHeight,
          setModalHeight,
          messageModalHeight,
          setMessageModalHeight,
          shouldShowTutorialHashTag,
          setShouldShowTutorialHashTag,
          doNotShowTutorial,
          setDoNotShowTutorial,
          showChatOptionsBar,
          setShowChatOptionsBar,
          dropFileHandler,
          playingVoiceMemoId,
          setPlayingVoiceMemoId,
          subscribeToChatUpdates,
          getCameraPhoto,
          hasCameraPermission,
          setHasCameraPermission,
          refreshActivity,
          setRefreshActivity,
          myChatLocalFiles,
          setMyChatLocalFiles,
          myChatCheckin,
          setMyChatCheckin,
          editMedia,
          setEditMedia,
          uploadTagOnboardingStep,
          setUploadTagOnboardingStep,
          messageSelectionRef,
        } as ChatInputContext
      }>
      {children}
    </Provider>
  );
};

const useChatInput = (): ChatInputContext => {
  const context = useContext(chatInputContext);
  if (context === undefined) {
    throw new Error('useChatInput must be used within a Provider');
  }
  return context;
};

export default useChatInput;
